import { SearchResultItem } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { CompactPriceInfo, Image, SmartLink } from '@components/common';
import { useAuth, useCart } from '@hooks';

import { Box, Button, IconButton, Skeleton, styled, useMediaQuery, useTheme } from '@mui/material';
import { breakpoints, palette } from '@themes/theme.helpers';
import { t } from '@translations';
import { getImageResizerUrl } from '@utils/images.utils';

import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { checkIfExpired, ErpStatus, getErpStatus } from '@utils/products.utils';
import { MouseEvent } from 'react';

// Components & styles
const StyledItem = styled(Box)`
  border-bottom: 1px solid #f3f4f7;
  padding: 12px;
  text-align: left;
  color: #1c1c1c;
  display: flex;
  background: ${palette.common('white')};
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  ${breakpoints.up('md')} {
    margin: 0 0 1px 0;
    min-height: 50px;
  }

  &:hover {
    background: ${palette.primary('light')};
  }

  &,
  .price {
    font-size: 16px;
    font-weight: 400;
    line-height: 26px;
    letter-spacing: 0.01em;
  }

  .button {
    height: 32px !important;
    padding-left: 16px;
    padding-right: 16px;
    min-width: auto !important;
  }
`;

const ImageBox = styled(Box)`
  display: inline-block;
  margin-right: 16px;
  width: 40px;
  height: 40px;
  .epi-image {
    width: 40px;
    height: 40px;
  }
`;

export type SearchItemProps = {
  item?: SearchResultItem;
  showPrices?: boolean;
  showImages?: boolean;
  showProductNo?: boolean;
  download?: boolean;
  shouldRenderAsRouterLink?: boolean;
};

export const QuickSearchResultsItem = ({
  item = {},
  showPrices,
  showImages,
  showProductNo,
  download,
  shouldRenderAsRouterLink,
}: SearchItemProps) => {
  const url = item?.url || '';
  const imageUrl = item.image ? getImageResizerUrl(item.image, { width: 40 }) : '';
  const { isAuthenticated, isBeremaUser } = useAuth();
  const { addToCart } = useCart();
  const theme = useTheme();
  const isBreakpointSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const erpStatus = getErpStatus(item.itemERPItemStatus as string) as ErpStatus;
  const isExpired = checkIfExpired(erpStatus);
  const shouldDisplayAddToCart =
    showPrices &&
    isAuthenticated &&
    !isExpired &&
    !isBeremaUser &&
    isAuthenticated &&
    item?.discountPrice?.amount;
  const addToCartHandler = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(item.code, 1, item.name);
  };

  const content = (
    <StyledItem>
      {showImages && (
        <ImageBox>
          <Image
            image={imageUrl}
            scalingMode='fit'
            resizerParams={{ width: 40, format: 'webp' }}
            width='40'
            height='40'
          />
        </ImageBox>
      )}
      <Box display='flex' flex='1' flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
        <Box component='span' flex='1' flexDirection='column'>
          <Box fontWeight='bold'>{item.displayName || item.name}</Box>
          {showProductNo && item.productNo && <Box>{item.productNo}</Box>}
        </Box>
        {showPrices && (
          <CompactPriceInfo
            grossPrice={item.grossPrice}
            discountPrice={item.discountPrice}
            isExpired={isExpired}
          />
        )}
      </Box>
      {shouldDisplayAddToCart && (
        <Box
          sx={{
            marginLeft: {
              xs: 'auto',
              sm: 4,
            },
          }}
        >
          {isBreakpointSmall ? (
            <IconButton
              onClick={addToCartHandler}
              sx={{
                background: '#ede9e7',
                color: '#515151',
              }}
            >
              <AddShoppingCartIcon fontSize='small' />
            </IconButton>
          ) : (
            <Button variant='tertiary' className='button' onClick={addToCartHandler}>
              {t(TextCodes.Search.QuickAdd)}
            </Button>
          )}
        </Box>
      )}
    </StyledItem>
  );
  if (shouldRenderAsRouterLink) {
    return (
      <SmartLink
        href={url}
        sx={{
          display: 'block',
          cursor: 'pointer',
        }}
        download={download}
      >
        {content}
      </SmartLink>
    );
  }
  return content;
};

export const SearchItemPlaceholder = () => <Skeleton />;
